import React from "react";
import { Input } from "components/Graphs/Input";

import { FadeInDiv } from "components/Motion/FadeInDiv";
import {
  ArchiveBoxArrowDownIcon,
  CommandLineIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { RadioCardItem } from "components/Graphs/RadioCardGroup";
import { RadioCardGroup } from "components/Graphs/RadioCardGroup";
import { GitHubLogoIcon } from "@radix-ui/react-icons";
import { validateFileUrl } from "../../utils";
interface CodePathFormProps {
  codeSource: string;
  onCodeSourceChange: (source: string) => void;
  codeSourceType: "url/upload" | "embedded" | "none" | "";
  setCodeSourceType: (type: "url/upload" | "embedded" | "none") => void;
  codeSourceError: string | null;
  setCodeSourceError: (error: string | null) => void;
  showEmbeddedCodeOption?: boolean;
}

export const CodePathForm: React.FC<CodePathFormProps> = ({
  codeSource,
  onCodeSourceChange,
  codeSourceType,
  setCodeSourceType,
  codeSourceError,
  setCodeSourceError,
  showEmbeddedCodeOption = false,
}) => {
  const checkCodeSourceError = (value: string) => {
    const error = validateFileUrl(value);
    setCodeSourceError(error || null);
  };

  return (
    <div>
      <h2 className="mb-4 text-base font-medium text-white">
        How would you like to provide your code files?
      </h2>

      <RadioCardGroup
        key={"code-source"}
        value={codeSourceType}
        onValueChange={(value) =>
          setCodeSourceType(value as "url/upload" | "embedded" | "none")
        }
        className={`grid grid-cols-1 gap-4 ${
          showEmbeddedCodeOption ? "md:grid-cols-3" : "md:grid-cols-2"
        }`}
      >
        <RadioCardItem
          value="none"
          className="cursor-pointer hover:border-highlight transition-colors"
        >
          <div className="flex flex-row items-start justify-center space-y-1">
            <CommandLineIcon className="h-5 w-5 text-primary mr-1" />
            <p className="text-sm font-medium leading-none text-primary">
              {"I don't have any code files"}
            </p>
          </div>
        </RadioCardItem>

        <RadioCardItem
          value="url/upload"
          className="cursor-pointer hover:border-highlight transition-colors"
        >
          <div className="flex flex-row items-start justify-center space-y-1">
            <GitHubLogoIcon className="h-5 w-5 text-primary mr-1" />
            <p className="text-sm font-medium leading-none text-primary">
              I have code files in a git repository
            </p>
          </div>
        </RadioCardItem>

        {showEmbeddedCodeOption && (
          <RadioCardItem
            value="embedded"
            className="cursor-pointer hover:border-highlight transition-colors"
          >
            <div className="flex flex-row items-start justify-center space-y-1">
              <ArchiveBoxArrowDownIcon className="h-5 w-5 text-primary mr-1" />
              <p className="text-sm font-medium leading-none text-primary">
                My code files are embedded in my container(s)
              </p>
            </div>
          </RadioCardItem>
        )}
      </RadioCardGroup>

      <div className="mt-6">
        {codeSourceType === "url/upload" && (
          <FadeInDiv className="space-y-4">
            <div>
              <h3 className="text-primary text-sm mb-1">
                Enter a public repository, notebook file, or markdown file URL
              </h3>
              <Input
                id="repo-url"
                placeholder="https://"
                value={codeSource}
                onChange={(e) => {
                  onCodeSourceChange(e.target.value.trim());
                  checkCodeSourceError(e.target.value.trim());
                }}
              />
              {codeSourceError && (
                <p className="text-xs text-red-500 mt-1">{codeSourceError}</p>
              )}
              <p className="text-xs text-secondary mt-1">
                Examples: https://github.com/username/repo.git or
                https://github.com/username/repo/blob/main/notebook.ipynb
              </p>
            </div>
          </FadeInDiv>
        )}

        {codeSourceType === "embedded" && (
          <FadeInDiv className="flex flex-col items-center justify-center w-full">
            <div className="p-3 rounded-md text-sm text-secondary bg-secondaryBg flex flex-col w-full">
              <div className="flex flex-row text-amber-500">
                <ExclamationTriangleIcon className="h-5 w-5 mr-1" />
                <h2 className="text-sm">Warning: Embedded Code Files</h2>
              </div>
              <p className="text-sm text-secondary mt-2">
                Code file artifacts within a container{" "}
                <strong>will not show up</strong> on the deploy page for your
                launchable and is <strong>not recommended</strong>.
              </p>
              <p className="text-sm text-secondary">
                For the best user experience, we suggest adding your code files
                to a <strong>git repository</strong> and using the git
                repository option above.
              </p>
            </div>
          </FadeInDiv>
        )}
      </div>
    </div>
  );
};
